import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";

/**
 * HorizontalTabsScroller — Auto-cycling (fade) tabs for WhyChooseUsPage
 *
 * Props:
 *  - palette: { primary, mint, mid, ink }
 *  - onConsult: () => void
 *  - autoCycle: boolean (default true)
 *  - cycleInterval: number ms (default 6000)
 *  - pauseOnHover: boolean (default true)
 */

const DEFAULT_PALETTE = {
  primary: "#2E6F40",
  mint: "#CFFFDC",
  mid: "#68BA7F",
  ink: "#253D2C",
};

export default function HorizontalTabsScroller({
  palette = DEFAULT_PALETTE,
  onConsult = () => {
    try { document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); } catch {}
  },
  autoCycle = true,
  cycleInterval = 6000,
  pauseOnHover = true,
}) {
  const COLORS = useMemo(() => ({ ...DEFAULT_PALETTE, ...palette }), [palette]);

  const TABS = [
    {
      key: "expertise",
      label: "Expertise",
      h: "Expertise",
      body: [
        "Multi-disciplinary counsel across civil, criminal, corporate, family, IP, cyber, and arbitration.",
        "We map facts to forum, law, and feasible outcomes—so your matter is argued where it’s strongest.",
        "From strategy memos to court-ready submissions, our work product travels across jurisdictions.",
      ],
      images: [
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1600&auto=format&fit=crop",
      ],
    },
    {
      key: "execution",
      label: "Execution",
      h: "Execution",
      body: [
        "Speed with discipline—clear owners, timelines, and filing checklists for every stage.",
        "We prepare evidence like litigators and sequence actions like project managers.",
        "You get milestone summaries, next steps, and an escalation path for urgent issues.",
      ],
      images: [
        "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?q=80&w=1600&auto=format&fit=crop",
      ],
    },
    {
      key: "impact",
      label: "Impact",
      h: "Impact",
      body: [
        "Outcome-oriented advice: reliefs sought are realistic, proportionate, and strategically sequenced.",
        "We quantify risks and trade-offs so you can choose with confidence.",
        "Reporting focuses on decisions, not noise—what changed, why it matters, and what we do next.",
      ],
      images: [
        "https://images.unsplash.com/photo-1450101215322-bf5cd27642fc?q=80&w=1600&auto=format&fit=crop",
      ],
    },
    {
      key: "ideology",
      label: "Ideology / Aim",
      h: "Ideology & Aim",
      body: [
        "Principled advocacy—client interest, professional ethics, and procedural fairness come first.",
        "We balance law, technology, and human rights to turn complex problems into actionable strategy.",
        "Clarity before action: you always know the plan, the risk, and the why.",
      ],
      images: [
        "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop",
      ],
    },
  ];

  const [active, setActive] = useState(0);
  const [imgIndex, setImgIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const hoverRef = useRef(false);
  const intervalRef = useRef(null);

  const tab = TABS[active];

  const goTo = useCallback((i) => {
    setFading(true);
    setTimeout(() => {
      setActive(i);
      setImgIndex(0);
      setFading(false);
    }, 250); // half of duration for smoother crossfade
  }, []);

  const nextTab = useCallback(() => {
    goTo((active + 1) % TABS.length);
  }, [active, TABS.length, goTo]);

  const prevTab = useCallback(() => {
    goTo((active - 1 + TABS.length) % TABS.length);
  }, [active, TABS.length, goTo]);

  // auto-cycle
  useEffect(() => {
    if (!autoCycle) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (pauseOnHover && hoverRef.current) return;
      nextTab();
    }, Math.max(2000, cycleInterval));
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [autoCycle, cycleInterval, pauseOnHover, nextTab]);

  const onEnter = () => { if (pauseOnHover) hoverRef.current = true; };
  const onLeave = () => { if (pauseOnHover) hoverRef.current = false; };

  return (
    <section className="relative py-16 bg-white" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Tabs Row */}
        <div className="flex flex-wrap justify-center gap-3 border-b pb-4 mb-8"
             style={{ borderColor: COLORS.mid }}>
          {TABS.map((t, i) => (
            <button
              key={t.key}
              onClick={() => goTo(i)}
              className={`px-4 py-2 rounded-xl text-sm md:text-base transition font-medium ${
                i === active ? "shadow-sm" : "opacity-70 hover:opacity-100"
              }`}
              style={{
                background: i === active ? COLORS.mint : "transparent",
                color: COLORS.ink,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content Row */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center transition-opacity duration-500 ${fading ? 'opacity-0' : 'opacity-100'}`}>
          {/* Left Text Side */}
          <div>
            <h3 className="text-2xl md:text-3xl font-semibold mb-4" style={{ color: COLORS.ink }}>
              {tab.h}
            </h3>
            <button
              onClick={onConsult}
              className="mb-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
              style={{ background: COLORS.primary, color: "#fff" }}
            >
              Book a Consultation
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
            <div className="space-y-3 md:space-y-4">
              {tab.body.map((p, idx) => (
                <p key={idx} className="text-sm md:text-base leading-relaxed" style={{ color: COLORS.ink }}>
                  {p}
                </p>
              ))}
            </div>
          </div>

          {/* Right Image Side */}
          <div className="relative rounded-2xl border overflow-hidden aspect-[4/3]"
               style={{ borderColor: COLORS.mid, background: "#f6f8f7" }}>
            <img
              key={`${tab.key}-${imgIndex}-${active}`}
              src={tab.images[imgIndex]}
              alt={tab.h}
              className={`w-full h-full object-cover transition-opacity duration-500 ${fading ? 'opacity-0' : 'opacity-100'}`}
            />

            {/* Arrows to move between tabs */}
            <button
              aria-label="Previous tab"
              onClick={prevTab}
              className="absolute left-3 top-1/2 -translate-y-1/2 grid place-items-center w-9 h-9 rounded-full shadow-md border backdrop-blur bg-white/80"
              style={{ borderColor: COLORS.mid, color: COLORS.ink }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              aria-label="Next tab"
              onClick={nextTab}
              className="absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center w-9 h-9 rounded-full shadow-md border backdrop-blur bg-white/80"
              style={{ borderColor: COLORS.mid, color: COLORS.ink }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
